import { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/button';

// Ads CSV export modal (docs/GoogleCsvExport_Spec.md §2.2). Three
// pieces of state: scope (filtered vs all), Campaign name template
// (default "Kyck | Search | {suburb}", hardcoded — NOT brand-config,
// NOT user-sticky), and Ad Group name (default "Brand"). Counts are
// derived upstream from the Google grid's gate + current filter —
// passed in so the modal stays display-only.
//
// Generate CSV is disabled when the selected scope has 0 rows OR
// either text input is empty after trim (an empty Ad Group would
// fail Google Ads Editor's import; an empty Campaign template would
// produce a blank Campaign column). Dismissed via Cancel, ESC, or
// backdrop click.

export type ExportScope = 'filtered' | 'all';

// Default value lives in src/modules/rollouts/exportDefaults.ts so
// the Budget modal + any future CSV exports stay in lockstep.
export const DEFAULT_CAMPAIGN_NAME_TEMPLATE = 'Kyck | Search | {suburb}';
export const CAMPAIGN_NAME_TEMPLATE_HINT =
  'Available placeholders: {suburb}, {locationName}, {monthYear}, {shortSuburb}';

type Props = {
  filteredCount: number;
  gateCount: number;
  busy?: boolean;
  // Pre-export validation: violation counts per scope. When the
  // user-selected scope has any violations, the modal flips to a
  // "View Errors / Export Anyway" gate. Optional — modals render the
  // existing flow when both are 0 / undefined.
  filteredViolationCount?: number;
  filteredLocationCount?: number;
  gateViolationCount?: number;
  gateLocationCount?: number;
  onCancel: () => void;
  onGenerate: (args: {
    scope: ExportScope;
    campaignNameTemplate: string;
    adGroup: string;
  }) => void;
  // Hand-off to the grid's errors modal. Carries the scope the AM
  // selected so the errors modal opens with the same row set.
  onShowErrors?: (scope: ExportScope) => void;
};

export function GoogleAdsExportModal({
  filteredCount,
  gateCount,
  busy,
  filteredViolationCount = 0,
  filteredLocationCount = 0,
  gateViolationCount = 0,
  gateLocationCount = 0,
  onCancel,
  onGenerate,
  onShowErrors,
}: Props) {
  const [scope, setScope] = useState<ExportScope>('filtered');
  const [campaignNameTemplate, setCampaignNameTemplate] = useState<string>(
    DEFAULT_CAMPAIGN_NAME_TEMPLATE,
  );
  const [adGroup, setAdGroup] = useState<string>('Brand');
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const selectedCount = scope === 'filtered' ? filteredCount : gateCount;
  const trimmedAdGroup = adGroup.trim();
  const trimmedTemplate = campaignNameTemplate.trim();
  const violationCount =
    scope === 'filtered' ? filteredViolationCount : gateViolationCount;
  const locationCount =
    scope === 'filtered' ? filteredLocationCount : gateLocationCount;
  const hasViolations = violationCount > 0;
  const disabled =
    busy ||
    selectedCount === 0 ||
    trimmedAdGroup.length === 0 ||
    trimmedTemplate.length === 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={() => !busy && onCancel()}
    >
      <div
        className="w-full max-w-lg rounded-md bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="google-ads-export-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 space-y-5">
          <h2
            id="google-ads-export-title"
            className="text-lg font-semibold text-slate-900"
          >
            Export Ads CSV
          </h2>

          {hasViolations && (
            <div
              role="alert"
              className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              ⚠ {violationCount} validation {violationCount === 1 ? 'error' : 'errors'} found
              {' '}across {locationCount} {locationCount === 1 ? 'location' : 'locations'}.
            </div>
          )}

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-slate-700">Scope</legend>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="radio"
                name="ads-export-scope"
                value="filtered"
                checked={scope === 'filtered'}
                onChange={() => setScope('filtered')}
                disabled={busy}
                className="mt-0.5"
              />
              <span>Currently filtered rows ({filteredCount} {filteredCount === 1 ? 'row' : 'rows'})</span>
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="radio"
                name="ads-export-scope"
                value="all"
                checked={scope === 'all'}
                onChange={() => setScope('all')}
                disabled={busy}
                className="mt-0.5"
              />
              <span>All gate-passing rows ({gateCount} {gateCount === 1 ? 'row' : 'rows'})</span>
            </label>
          </fieldset>

          <div className="space-y-1">
            <label
              htmlFor="ads-export-campaign-template"
              className="block text-sm font-medium text-slate-700"
            >
              Campaign name template
            </label>
            <input
              id="ads-export-campaign-template"
              type="text"
              value={campaignNameTemplate}
              onChange={(e) => setCampaignNameTemplate(e.target.value)}
              disabled={busy}
              maxLength={500}
              className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
            />
            <p className="text-xs text-slate-500">{CAMPAIGN_NAME_TEMPLATE_HINT}</p>
          </div>

          <div className="space-y-1">
            <label
              htmlFor="ads-export-ad-group"
              className="block text-sm font-medium text-slate-700"
            >
              Ad Group name
            </label>
            <input
              id="ads-export-ad-group"
              type="text"
              value={adGroup}
              onChange={(e) => setAdGroup(e.target.value)}
              disabled={busy}
              maxLength={120}
              className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
            />
            <p className="text-xs text-slate-500">
              All ads will be assigned to this ad group in Google Ads.
            </p>
          </div>

          {selectedCount === 0 && (
            <p className="text-sm text-amber-700">No rows match the selected scope.</p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              ref={cancelRef}
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            {hasViolations ? (
              <>
                <button
                  type="button"
                  onClick={() =>
                    onGenerate({
                      scope,
                      campaignNameTemplate: trimmedTemplate,
                      adGroup: trimmedAdGroup,
                    })
                  }
                  disabled={disabled}
                  className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-50 disabled:opacity-50"
                >
                  {busy ? 'Generating…' : 'Export Anyway'}
                </button>
                <Button
                  onClick={() => onShowErrors?.(scope)}
                  disabled={busy || onShowErrors === undefined}
                >
                  View Errors
                </Button>
              </>
            ) : (
              <Button
                onClick={() =>
                  onGenerate({
                    scope,
                    campaignNameTemplate: trimmedTemplate,
                    adGroup: trimmedAdGroup,
                  })
                }
                disabled={disabled}
              >
                {busy ? 'Generating…' : 'Generate CSV'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
